import {
  useState,
  useEffect,
  useCallback,
  useRef,
  CompositionEvent,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  usePatient,
  useSelectPatient,
  useVerifyPatientInvitationLastName,
} from '@/hooks/patient.hooks';
import { useInvitation } from '@/hooks/useInvitation';
import {
  updateInvitationId,
  updateQuestionnaireId,
  updateQuestionnaireRevision,
  updateConsultationVariant,
  setMode,
  setInvitationFallback,
  setPatientSegment,
} from '@/reducers/graph';
import { useInjectConsultation } from '@/hooks/useInjectConsultation';
import { RootState } from '@/types/graph';
import { LETTERS_WITH_DIACRITIC_REGEX } from '@aldebaran/common/util/regex';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { SHARED_ROUTES } from '@/routes';

type AuthTerminalState = 'success' | 'error' | 'too-many-tries' | null;

/** Trigger scenarios for the fallback button */
type FallbackTrigger =
  | 'exhausted_attempts'
  | 'two_failed_attempts'
  | 'idle_timeout'
  | 'repeated_refresh'
  | null;

/** Idle timeout in milliseconds (30 seconds) */
const IDLE_TIMEOUT_MS = 30_000;

/** Number of refreshes required to show fallback */
const REFRESH_THRESHOLD = 2;

/** Session storage key for refresh count */
const REFRESH_COUNT_KEY = 'invitation_auth_refresh_count';

/** Session storage key for refresh timestamp */
const REFRESH_TIMESTAMP_KEY = 'invitation_auth_refresh_timestamp';

/** TTL for refresh count (30 seconds) */
const REFRESH_TTL_MS = 30_000;

export interface UseInvitationAuthenticationReturn {
  professional?: RootState['consultation']['professional'];
  practice?: RootState['consultation']['practice'];
  lastName: string;
  error: string;
  isComposing: boolean;
  handleChange: (value: string) => void;
  handleCompositionStart: () => void;
  handleCompositionEnd: (e: CompositionEvent<HTMLInputElement>) => void;
  isVerifyPending: boolean;
  isVerifySuccess: boolean;
  showFallback: boolean;
  fallbackTrigger: FallbackTrigger;
  handleFallback: () => void;
}

export const useInvitationAuthentication =
  (): UseInvitationAuthenticationReturn => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const posthog = usePostHog();
    const { navigateNext } = useQuestionnaireNavigation();
    const invitationId = useSelector((state: RootState) => state.invitationId);
    const professional = useSelector(
      (state: RootState) => state.consultation.professional,
    );
    const practice = useSelector(
      (state: RootState) => state.consultation.practice,
    );
    const injectConsultation = useInjectConsultation({ required: false });

    // Fallback tracking state
    const [showIdleFallback, setShowIdleFallback] = useState(false);
    const [showRefreshFallback, setShowRefreshFallback] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const mountTimeRef = useRef<number>(Date.now());
    const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const attemptCountRef = useRef<number>(0);

    const {
      mutate: verifyLastNameMutate,
      data: verifyStatus,
      error: verifyError,
      isLoading: isVerifyPending,
    } = useVerifyPatientInvitationLastName(false);

    const [error, setError] = useState('');
    const [lastName, setLastName] = useState<string>('');
    const { data: invitation } = useInvitation(invitationId || '');
    const [patientId, setPatientId] = useState<string | null>(null);
    const { data: patient } = usePatient(patientId);
    const { mutate: selectPatient } = useSelectPatient({ required: false });
    const [isComposing, setIsComposing] = useState(false);
    const hasClaimedRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const terminalStateRef = useRef<AuthTerminalState>(null);
    const lastVerifiedValueRef = useRef<string>('');

    // Reset idle timeout function
    const resetIdleTimeout = useCallback(() => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      idleTimeoutRef.current = setTimeout(() => {
        setShowIdleFallback(true);
      }, IDLE_TIMEOUT_MS);
    }, []);

    // Initialize idle timeout on mount
    useEffect(() => {
      resetIdleTimeout();
      return () => {
        if (idleTimeoutRef.current) {
          clearTimeout(idleTimeoutRef.current);
        }
      };
    }, [resetIdleTimeout]);

    // Detect repeated refreshes on mount
    useEffect(() => {
      const now = Date.now();
      const storedTimestamp = sessionStorage.getItem(REFRESH_TIMESTAMP_KEY);
      const storedCount = sessionStorage.getItem(REFRESH_COUNT_KEY);

      let refreshCount = 0;

      if (storedTimestamp && storedCount) {
        const timestamp = parseInt(storedTimestamp, 10);
        const count = parseInt(storedCount, 10);

        // Check if within TTL
        if (now - timestamp < REFRESH_TTL_MS) {
          refreshCount = count + 1;
        }
      } else {
        refreshCount = 0;
      }

      // Update session storage
      sessionStorage.setItem(REFRESH_TIMESTAMP_KEY, now.toString());
      sessionStorage.setItem(REFRESH_COUNT_KEY, refreshCount.toString());

      // Show fallback if threshold reached
      if (refreshCount >= REFRESH_THRESHOLD) {
        setShowRefreshFallback(true);
      }
    }, []);

    useEffect(() => {
      if (
        isComposing ||
        !invitationId ||
        lastName.length <= 1 ||
        terminalStateRef.current !== null ||
        isVerifyPending
      ) {
        return;
      }

      if (lastName === lastVerifiedValueRef.current) {
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (terminalStateRef.current !== null) return;
        lastVerifiedValueRef.current = lastName;
        attemptCountRef.current += 1;
        verifyLastNameMutate({ invitationId, lastName });
      }, 300);

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, [
      lastName,
      isComposing,
      invitationId,
      verifyLastNameMutate,
      isVerifyPending,
    ]);

    // Handle verification status and errors
    useEffect(() => {
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      if ((verifyError as any)?.response?.data?.message === 'Too many tries') {
        setError('too-many-tries');
        terminalStateRef.current = 'too-many-tries';
      } else if (verifyError || verifyStatus?.data.status === 'error') {
        setError('error');
        terminalStateRef.current = 'error';
      } else if (verifyStatus?.data.status === 'fail') {
        setError('fail');
        setFailedAttempts((prev) => prev + 1);
      } else if (
        verifyStatus?.data.status === 'success' &&
        invitation?.consultation.patient.id
      ) {
        setError('');
        terminalStateRef.current = 'success';
        setPatientId(invitation.consultation.patient.id);
      } else {
        setError('');
      }

      // Clear idle timeout once we reach a terminal state
      if (terminalStateRef.current !== null && idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
    }, [verifyStatus, verifyError, invitation, setPatientId, setError]);

    // Claim invitation on success and navigate next
    useEffect(() => {
      if (patient && verifyStatus && !hasClaimedRef.current) {
        const questionnaireId = verifyStatus.data?.questionnaireId;
        if (invitation && questionnaireId) {
          hasClaimedRef.current = true;
          dispatch(updateInvitationId(invitation.id));
          dispatch(updateQuestionnaireId(questionnaireId));
          dispatch(updateQuestionnaireRevision(verifyStatus.data.revision));
          // The server may have switched the consultation to its continuation
          // variant (a borne check-in continuing on the phone, ALDO-2759)
          if (verifyStatus.data.variant)
            dispatch(updateConsultationVariant(verifyStatus.data.variant));
          injectConsultation({ date: invitation.consultation.date });

          // Clear refresh tracking on successful auth
          sessionStorage.removeItem(REFRESH_COUNT_KEY);
          sessionStorage.removeItem(REFRESH_TIMESTAMP_KEY);

          selectPatient(patient.id, {
            onSuccess: () => {
              navigateNext();
            },
          });
        }
      }
    }, [
      patient,
      verifyStatus,
      invitation,
      dispatch,
      injectConsultation,
      selectPatient,
      navigateNext,
    ]);

    const handleChange = useCallback(
      (value: string) => {
        if (
          terminalStateRef.current === 'success' ||
          terminalStateRef.current === 'error' ||
          terminalStateRef.current === 'too-many-tries'
        ) {
          return;
        }
        // Reset idle timeout on user input
        resetIdleTimeout();

        if (isComposing) {
          setLastName(value);
        } else if (value === '' || LETTERS_WITH_DIACRITIC_REGEX.test(value)) {
          setLastName(value);
        }
      },
      [isComposing, resetIdleTimeout],
    );

    // For compose keys
    const handleCompositionStart = useCallback(() => {
      setIsComposing(true);
    }, []);

    const handleCompositionEnd = useCallback(
      (e: CompositionEvent<HTMLInputElement>) => {
        setIsComposing(false);
        handleChange(e.currentTarget.value);
      },
      [handleChange],
    );

    // Compute fallback trigger with priority: exhausted > failed > refresh > idle
    const fallbackTrigger: FallbackTrigger = useMemo(() => {
      if (error === 'too-many-tries' || error === 'error') {
        return 'exhausted_attempts';
      }
      if (failedAttempts >= 2) {
        return 'two_failed_attempts';
      }
      if (showRefreshFallback) {
        return 'repeated_refresh';
      }
      if (showIdleFallback) {
        return 'idle_timeout';
      }
      return null;
    }, [showRefreshFallback, showIdleFallback, error, failedAttempts]);

    const showFallback = fallbackTrigger !== null;

    // Handle fallback button click
    const handleFallback = useCallback(() => {
      if (!fallbackTrigger) return;

      const timeOnPage = (Date.now() - mountTimeRef.current) / 1000;

      // Capture PostHog event
      posthog?.capture(`invitation_auth_fallback_${fallbackTrigger}`, {
        invitationId,
        professionalId: professional?.id,
        practiceId: practice?.id,
        attemptCount: attemptCountRef.current,
        timeOnPage,
      });

      // Clear invitation state
      dispatch(updateInvitationId(null));
      dispatch(setPatientSegment(null));

      // Mark as invitation fallback for tracking
      dispatch(setInvitationFallback(true));

      // Switch to LINK mode (prefer PROFESSIONAL_LINK, only use PRACTICE_LINK if no professional)
      dispatch(setMode(professional ? 'PROFESSIONAL_LINK' : 'PRACTICE_LINK'));

      // Clear refresh tracking
      sessionStorage.removeItem(REFRESH_COUNT_KEY);
      sessionStorage.removeItem(REFRESH_TIMESTAMP_KEY);

      // Navigate to landing professional select
      navigate(SHARED_ROUTES.LANDING_PROFESSIONAL_SELECT);
    }, [
      fallbackTrigger,
      invitationId,
      professional,
      practice,
      dispatch,
      navigate,
      posthog,
    ]);

    return {
      professional,
      practice,
      lastName,
      error,
      isComposing,
      handleChange,
      handleCompositionStart,
      handleCompositionEnd,
      isVerifyPending,
      isVerifySuccess: verifyStatus?.data.status === 'success',
      showFallback,
      fallbackTrigger,
      handleFallback,
    };
  };
